// /audit — combined audit_log ∪ staff_audit_log feed.
// Filter bar at top (action prefix, source, tenant id), table below.
// Each row is expandable to show the full meta JSON.

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useApiClient } from '../../lib/providers.js';
import {
  AUDIT_QUERY_KEY,
  platformApi,
  sourceBadgeVariant,
  type AuditRow,
  type AuditSource,
  type ListAuditParams,
} from '../../lib/api/platform.js';
import { Badge } from '../../components/ui/badge.js';
import { Card, CardContent } from '../../components/ui/card.js';
import { Input } from '../../components/ui/input.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { EmptyState } from '../../components/ui/empty-state.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { formatDateTime } from '../../lib/format-date.js';

export function AuditPage() {
  const client = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionInput, setActionInput] = useState(searchParams.get('actionPrefix') ?? '');

  const filters: ListAuditParams = useMemo(() => {
    const ap = searchParams.get('actionPrefix');
    const src = searchParams.get('source');
    return {
      ...(ap ? { actionPrefix: ap } : {}),
      ...(src ? { source: src as AuditSource } : {}),
      sinceHours: 168,
      limit: 200,
    };
  }, [searchParams]);

  const query = useQuery({
    queryKey: [...AUDIT_QUERY_KEY, filters] as const,
    queryFn: () => platformApi.listAudit(client, filters),
  });

  function setFilter(k: string, v: string | null): void {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v === null || v.length === 0) next.delete(k);
      else next.set(k, v);
      return next;
    }, { replace: true });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit log"
        description="Combined customer + staff audit feed across every tenant. Last 7 days."
      />

      <div className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-end">
        <form
          className="flex flex-1 flex-col gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter('actionPrefix', actionInput.trim() || null);
          }}
        >
          <Label className="text-xs text-muted-foreground">Action prefix</Label>
          <Input
            value={actionInput}
            onChange={(e) => {
              setActionInput(e.target.value);
            }}
            onBlur={() => {
              setFilter('actionPrefix', actionInput.trim() || null);
            }}
            placeholder="e.g. tenant., billing., prompt_injection_"
          />
        </form>
        <div className="flex flex-col gap-1.5 sm:w-48">
          <Label className="text-xs text-muted-foreground">Source</Label>
          <Select
            value={filters.source ?? 'all'}
            onChange={(e) => {
              const v = e.target.value;
              setFilter('source', v === 'all' ? null : v);
            }}
          >
            <option value="all">All sources</option>
            <option value="tenant">Tenant (customer-side)</option>
            <option value="staff">Staff (operator)</option>
          </Select>
        </div>
      </div>

      {query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : query.isError ? (
        <ErrorRetry
          title="Failed to load audit log"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data.rows.length === 0 ? (
        <EmptyState
          title="No audit entries"
          description="Loosen the filters or wait for activity in the last 7 days."
        />
      ) : (
        <Card>
          <CardContent className="px-0 pb-0">
            <AuditTable rows={query.data.rows} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AuditTable({ rows }: { rows: readonly AuditRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-6"></TableHead>
          <TableHead>Time</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Tenant</TableHead>
          <TableHead>Actor</TableHead>
          <TableHead>Target</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <ExpandableRow key={`${row.source}:${row.id}`} row={row} />
        ))}
      </TableBody>
    </Table>
  );
}

function ExpandableRow({ row }: { row: AuditRow }) {
  const [open, setOpen] = useState(false);
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
      >
        <TableCell className="px-2">
          <Icon aria-hidden className="h-4 w-4 text-muted-foreground" />
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
          {formatDateTime(row.ts)}
        </TableCell>
        <TableCell>
          <Badge variant={sourceBadgeVariant(row.source)}>{row.source}</Badge>
        </TableCell>
        <TableCell className="text-xs font-mono">{row.action}</TableCell>
        <TableCell>
          {row.tenantId ? (
            <Link to={`/tenants/${row.tenantId}`} className="hover:text-foreground">
              {row.tenantName ?? row.tenantId.slice(0, 8)}
            </Link>
          ) : (
            <span className="text-xs italic text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {row.actorEmail ?? row.actorId ?? '—'}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {row.target ?? '—'}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={7} className="bg-muted/20 p-3">
            <pre className="overflow-auto rounded-md border bg-background p-3 text-xs">
              {JSON.stringify(row.meta, null, 2)}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
